import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Field, formatDateTime } from "@/components/admin/ui";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const selectClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";

export const JobDetailPanel = ({ jobId, meta, API, onChanged, statusMeta, confirmationLabels, appointmentTypeLabels }) => {
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/org/office/jobs/${jobId}`, { withCredentials: true })
      .then(({ data }) => {
        setJob(data.job);
        setStatus(data.job.status);
        setConfirmation(data.job.customer_confirmation || "not_contacted");
        setAssignedUserIds(data.job.assigned_user_ids || []);
        setNotes(data.job.notes || "");
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, jobId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="office-job-detail-error">{error}</p>;
  if (!job) return <p className="text-sm text-slate-400">Loading…</p>;

  const teamMembers = meta.users.filter((u) => u.active && (u.team_ids || []).includes(job.team_id));
  const toggleUser = (userId) => setAssignedUserIds((ids) => ids.includes(userId) ? ids.filter((i) => i !== userId) : [...ids, userId]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const body = {};
      if (status !== job.status) body.status = status;
      if (confirmation !== (job.customer_confirmation || "not_contacted")) body.customer_confirmation = confirmation;
      if (JSON.stringify(assignedUserIds.slice().sort()) !== JSON.stringify((job.assigned_user_ids || []).slice().sort())) body.assigned_user_ids = assignedUserIds;
      if (notes !== (job.notes || "")) body.notes = notes;
      const { data } = await axios.patch(`${API}/org/office/jobs/${jobId}`, body, { withCredentials: true });
      setJob(data.job);
      onChanged();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="office-job-detail-panel">
      <div>
        <h3 className="text-lg font-bold text-slate-950 dark:text-white" data-testid="office-job-detail-title">{job.title}</h3>
        {job.customer_name && <p className="text-sm text-slate-500 dark:text-slate-400">{job.customer_name}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Team" value={job.team_name} testId="office-job-detail-team" />
        <Field label="Type" value={appointmentTypeLabels[job.appointment_type]} testId="office-job-detail-type" />
        <Field label="Start" value={formatDateTime(job.scheduled_start)} testId="office-job-detail-start" />
        <Field label="End" value={formatDateTime(job.scheduled_end)} testId="office-job-detail-end" />
        <Field label="Source" value={job.source === "sales" ? "Sales" : "Office"} testId="office-job-detail-source" />
        {job.lead_id && <Field label="Linked Lead" value={job.lead_id} testId="office-job-detail-lead" />}
      </div>

      {job.service_address && (
        <Field label="Service Address" value={`${job.service_address.street}, ${job.service_address.city}, ${job.service_address.state} ${job.service_address.zip_code}`} testId="office-job-detail-address" />
      )}

      <div>
        <label className={labelClass}>Status</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="office-job-detail-status-select" className={selectClass}>
          {meta.job_statuses.map((s) => <option key={s} value={s}>{statusMeta[s].label}</option>)}
        </select>
      </div>

      <div>
        <label className={labelClass}>Customer Confirmation</label>
        <select value={confirmation} onChange={(e) => setConfirmation(e.target.value)} data-testid="office-job-detail-confirmation-select" className={selectClass}>
          {meta.customer_confirmations.map((c) => <option key={c} value={c}>{confirmationLabels[c]}</option>)}
        </select>
      </div>

      <div>
        <label className={labelClass}>Assigned Team Members</label>
        <div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2 dark:border-white/15">
          {teamMembers.map((u) => (
            <label key={u.user_id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-white/5">
              <input type="checkbox" checked={assignedUserIds.includes(u.user_id)} onChange={() => toggleUser(u.user_id)} data-testid={`office-job-detail-assignee-${u.user_id}`} />
              {u.name}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className={labelClass}>Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="office-job-detail-notes-input"
          className="mt-1 w-full rounded-lg border border-slate-200 p-2.5 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
      </div>

      {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="office-job-detail-save-error">{error}</p>}

      <button type="button" onClick={save} disabled={saving} data-testid="office-job-detail-save-btn"
        className="w-full rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2.5 text-sm font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50">
        {saving ? "Saving…" : "Save Changes"}
      </button>

      <div className="border-t border-slate-200 pt-4 dark:border-white/10">
        <p className={labelClass}>Activity</p>
        <div className="mt-2 space-y-2" data-testid="office-job-detail-activity">
          {(job.activity || []).length === 0 ? (
            <p className="text-sm text-slate-400">No activity yet.</p>
          ) : (
            [...job.activity].reverse().map((a, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2.5 text-xs dark:border-white/10">
                <p className="font-semibold text-slate-700 dark:text-slate-200">{a.label}</p>
                <p className="mt-0.5 text-slate-400">{a.actor_name} · {formatDateTime(a.ts)}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
